import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon, toggleBtn } from '../../styles';
import { getBranches, getBranchSummary, getBranchTrend } from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── QUICK period buttons (same as other tabs) ────────────────────────────────
const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'         },
  { id: 'this_year',       label: 'This Year'          },
  { id: 'this_month',      label: 'This Month'         },
  { id: 'this_week',       label: 'This Week'          },
  { id: 'last_year',       label: 'Last Year'          },
  { id: 'last_month',      label: 'Last Month'         },
  { id: 'last_week',       label: 'Last Week'          },
  { id: 'last_year_month', label: "Last Year's Month"  },
  { id: 'custom',          label: 'Custom'             },
  { id: 'all',             label: 'All Time'           },
];

const BRANCH_COLORS = [
  '#a8e063', '#56ccf2', '#f2994a', '#bb6bd9', '#f2c94c',
  '#eb5757', '#27ae60', '#2d9cdb', '#9b51e0', '#f2994a',
];

// ─── custom legend helpers ────────────────────────────────────────────────────

const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const itemsKey = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [itemsKey]); // eslint-disable-line
  const toggle = (chartRef, name) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (chartRef) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (chartRef) => {
    const c = chartRef?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>
      All
    </button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>
      Reset
    </button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

// ─── KPI Card (matches SalesOverview style) ──────────────────────────────────
const KPICard = ({ title, value, iconBg, icon }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
  </div>
);

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  const v = parseFloat(n);
  return `RM ${v.toLocaleString(undefined, {maximumFractionDigits: 0})}`;
};

const fmtN = (n) => {
  if (!n) return '0';
  const v = parseInt(n);
  return v.toLocaleString();
};

// ─── Chart helpers ────────────────────────────────────────────────────────────
const chartBase = {
  backgroundColor: 'transparent',
  grid: { left: 8, right: 8, top: 16, bottom: 8, containLabel: true },
  tooltip: {
    trigger: 'axis',
    backgroundColor: C.bgElevated,
    borderColor: C.border,
    textStyle: { color: C.textPrimary, fontSize: 12 },
  },
};

const fmtDate = (str) => {
  // Axis label: "22 Apr" — no year, matches Sales tab style
  if (!str) return str;
  const d = new Date(str + 'T00:00:00');
  if (isNaN(d)) return str;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const fmtDateLong = (str) => {
  // Tooltip header: "22 Apr 2026" — full year, matches Sales tab style
  if (!str) return str;
  const d = new Date(str + 'T00:00:00');
  if (isNaN(d)) return str;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const generateFilledPeriodKeys = (sortedISO, granularity) => {
  if (!sortedISO.length) return sortedISO;
  const pad = n => String(n).padStart(2, '0');
  const parse = s => { const d = new Date(s + 'T00:00:00'); return isNaN(d) ? null : d; };
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const start = parse(sortedISO[0]);
  const end   = parse(sortedISO[sortedISO.length - 1]);
  if (!start || !end) return sortedISO;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(fmt(cur));
    if (granularity === 'monthly') cur.setMonth(cur.getMonth() + 1);
    else if (granularity === 'weekly') cur.setDate(cur.getDate() + 7);
    else cur.setDate(cur.getDate() + 1);
  }
  return result;
};

const xAxisBase = (data) => ({
  type: 'category',
  data,
  axisLabel: {
    color: C.textMuted, fontSize: 10, rotate: data.length > 8 ? 30 : 0,
    formatter: (val) => fmtDate(val),
  },
  axisLine: { lineStyle: { color: C.border } },
});

const yAxisBase = {
  type: 'value',
  axisLabel: {
    color: C.textMuted, fontSize: 10,
    formatter: (v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v,
  },
  splitLine: { lineStyle: { color: C.border, opacity: 0.4 } },
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────
// ─── sortable table helpers ───────────────────────────────────────────────────

const thBC = { padding:'8px 12px', fontSize:'11px', fontWeight:'600', color:'#6b7280', textTransform:'uppercase', borderBottom:'1px solid rgba(255,255,255,0.08)', cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' };

const SortTh = ({ col, label, sortCol, sortDir, onSort, align = 'right' }) => (
  <th onClick={() => onSort(col)} style={{ ...thBC, textAlign: align }}>
    {label}
    <span style={{ marginLeft:'4px', opacity: sortCol === col ? 1 : 0.3, fontSize:'9px' }}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
    </span>
  </th>
);

const useSortedData = (data, defaultCol, defaultDir = 'desc') => {
  const [sortCol, setSortCol] = useState(defaultCol);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const an = parseFloat(av), bn = parseFloat(bv);
    const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av || '').localeCompare(String(bv || ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return { sorted, sortCol, sortDir, toggleSort };
};

export default function BranchComparison() {
  // Period state — shared with other tabs via localStorage
  const [activePeriod, setAP] = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };

  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')         return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };

  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const [dateFrom, setDateFrom] = useState(_initFrom);
  const [dateTo,   setDateTo]   = useState(_initTo);
  const [pendingFrom, setPF]    = useState(_initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '');
  const [pendingTo,   setPT]    = useState(_initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '');

  const [trendPeriod, setTP] = useState(() => localStorage.getItem('ccs_gran') || 'daily');
  const setGran = (g) => { localStorage.setItem('ccs_gran', g); setTP(g); };

  // Force monthly for All Time
  useEffect(() => {
    if (activePeriod === 'all' && trendPeriod !== 'monthly') setGran('monthly');
  }, [activePeriod]);

  // Branch multi-select
  const [branches,      setBranches]      = useState([]);
  const [selectedBranches, setSelected]   = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [pendingBranches, setPending] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; }
  });
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop = () => { if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); } };
  const toggleBranch   = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches  = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches  = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  // Data state
  const [kpis,     setKpis]     = useState(null);
  const [byBranch, setByBranch] = useState([]);
  const [trend,    setTrend]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const trendChartRef     = useRef(null);
  const collectionPieRef  = useRef(null);

  // Load branches list
  useEffect(() => {
    getBranches()
      .then(r => setBranches(r.data.branches || []))
      .catch(() => {});
  }, []);

  // Compute dates when period changes
  useEffect(() => {
    if (activePeriod === 'custom') return;
    const [f, t] = _computeDates(activePeriod);
    setDateFrom(f);
    setDateTo(t);
  }, [activePeriod]);

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  const branchParam = selectedBranches.length > 0 ? selectedBranches.join(',') : 'all';

  const loadData = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (dateFrom)             p.date_from   = dateFrom;
    if (dateTo)               p.date_to     = dateTo;
    if (branchParam !== 'all') p.branch_code = branchParam;

    try {
      const [sumRes, trendRes] = await Promise.allSettled([
        getBranchSummary(p),
        getBranchTrend({ ...p, period: trendPeriod }),
      ]);
      if (sumRes.status === 'fulfilled') {
        setKpis(sumRes.value.data.kpis);
        setByBranch(sumRes.value.data.by_branch || []);
      }
      if (trendRes.status === 'fulfilled') {
        setTrend(trendRes.value.data.series || {});
      }
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, branchParam, trendPeriod]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Branch label helper ────────────────────────────────────────────────────
  const branchLabel = (code) => {
    const b = branches.find(x => x.code === code);
    return b ? (b.name || b.code) : code;
  };

  const branchBtnLabel = selectedBranches.length === 0
    ? 'All Branches'
    : selectedBranches.length === 1
    ? branchLabel(selectedBranches[0])
    : `${selectedBranches.length} Branches`;

  // ── table sort (component-level, Rules of Hooks) ──────────────────────────
  const { sorted: branchSorted, sortCol: bCol, sortDir: bDir, toggleSort: bSort } = useSortedData(byBranch, 'collection');

  // ── Branch name list + pie legend (must be after branchLabel) ─────────────
  const branchNames         = byBranch.map(b => branchLabel(b.branch_code));
  const collectionPieLegend = useChartLegend(branchNames);

  // ── Bar chart: Revenue by Branch (Total Sales A formula) ─────────────────
  const barRevenue = byBranch.length > 0 ? {
    ...chartBase,
    xAxis: xAxisBase(byBranch.map(b => branchLabel(b.branch_code))),
    yAxis: yAxisBase,
    series: [{
      type: 'bar',
      data: byBranch.map((b, i) => ({
        value: parseFloat(b.revenue || 0),
        itemStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length], borderRadius: [4,4,0,0] },
      })),
      barMaxWidth: 60,
    }],
  } : null;

  // ── Bar chart: Transactions by Branch ─────────────────────────────────────
  const barTxn = byBranch.length > 0 ? {
    ...chartBase,
    xAxis: xAxisBase(byBranch.map(b => branchLabel(b.branch_code))),
    yAxis: { ...yAxisBase, axisLabel: { ...yAxisBase.axisLabel, formatter: (v) => fmtN(v) } },
    series: [{
      type: 'bar',
      data: byBranch.map((b, i) => ({
        value: parseInt(b.transactions || 0),
        itemStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length], borderRadius: [4,4,0,0], opacity: 0.85 },
      })),
      barMaxWidth: 60,
    }],
  } : null;

  // ── Pie chart: Collection by Branch ──────────────────────────────────────
  const collectionPieOption = byBranch.length > 0 ? {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: p => `${p.name}<br/>RM ${Number(p.value).toLocaleString(undefined,{maximumFractionDigits:0})}<br/>${p.percent}%`,
    },
    legend: { show: false, data: branchNames },
    series: [{
      type: 'pie',
      radius: ['38%', '65%'],
      center: ['50%', '50%'],
      data: byBranch.map((b, i) => ({
        name:  branchLabel(b.branch_code),
        value: parseFloat(b.collection || 0),
        itemStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length] },
      })),
      label: { show: false },
    }],
  } : null;

  // ── Line chart: Revenue Trend per Branch ──────────────────────────────────
  const trendBranches = Object.keys(trend);
  const trendBranchLabels = trendBranches.map(branchLabel);
  const trendLegend = useChartLegend(trendBranchLabels);
  const allPeriods = generateFilledPeriodKeys(
    [...new Set(trendBranches.flatMap(b => (trend[b] || []).map(r => r.period)))].sort(),
    trendPeriod
  );

  const lineTrend = trendBranches.length > 0 ? {
    ...chartBase,
    tooltip: {
      trigger: 'axis',
      backgroundColor: C.bgElevated,
      borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDateLong(params[0]?.name);
        return lbl + '<br/>' + params.map(p =>
          `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>RM ${Number(p.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`
        ).join('<br/>');
      },
    },
    legend: { show: false },
    grid: { ...chartBase.grid, bottom: 8 },
    xAxis: xAxisBase(allPeriods),
    yAxis: yAxisBase,
    series: trendBranches.map((b, i) => {
      const dataMap = Object.fromEntries((trend[b] || []).map(r => [r.period, r.value]));
      return {
        name: branchLabel(b),
        type: 'line',
        smooth: true,
        data: allPeriods.map(p => dataMap[p] ?? 0),
        lineStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length], width: 2 },
        itemStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length] },
        areaStyle: { color: BRANCH_COLORS[i % BRANCH_COLORS.length], opacity: 0.08 },
        symbol: 'circle',
        symbolSize: 5,
      };
    }),
  } : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '16px', maxWidth: '1400px', margin: '0 auto' }}>

      {/* ── PERIOD PILLS ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
        {QUICK.map(q => (
          <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>
            {q.label}
          </button>
        ))}
      </div>

      {/* ── BRANCH MULTI-SELECT ── */}
      {branches.length > 0 && (
        <div style={{ marginBottom: '14px', position: 'relative', display: 'inline-block' }}>
          <button
            onClick={openBranchDrop}
            style={{
              ...filterPill(selectedBranches.length > 0),
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '6px 14px',
            }}
          >
            🏢 {branchBtnLabel}
            <span style={{ fontSize: '10px', opacity: 0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
          </button>

          {showBranchDrop && (
            <div style={{
              position: 'absolute', top: '38px', left: 0, zIndex: 999,
              background: C.bgElevated, border: `1px solid ${C.border}`,
              borderRadius: '12px', padding: '8px', minWidth: '180px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}>
              {branches.map(b => (
                <label key={b.code} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '7px 10px', cursor: 'pointer', borderRadius: '8px',
                  color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                  background: pendingBranches.includes(b.code) ? C.accentDim : 'transparent',
                  fontSize: '13px',
                }}>
                  <input
                    type="checkbox"
                    checked={pendingBranches.includes(b.code)}
                    onChange={() => toggleBranch(b.code)}
                    style={{ accentColor: C.accent }}
                  />
                  {b.name || b.code}
                </label>
              ))}
              <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '6px', paddingTop: '6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                <button onClick={applyBranches}
                  style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                    color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                  Apply
                </button>
                <button
                  onClick={resetBranches}
                  style={{ width: '100%', background: 'transparent', border: 'none',
                    color: C.textMuted, fontSize: '12px', cursor: 'pointer', padding: '4px 0' }}
                >
                  ↩ Reset to All
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── CUSTOM DATE ── */}
      {activePeriod === 'custom' && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-end' }}>
          {[['From', pendingFrom, setPF], ['To', pendingTo, setPT]].map(([label, val, setter]) => (
            <div key={label}>
              <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '4px', textTransform: 'uppercase' }}>{label}</div>
              <input type="date" value={val} onChange={e => setter(e.target.value)}
                style={{ background: C.bgElevated, border: `1px solid ${C.border}`, borderRadius: '8px',
                  color: C.textPrimary, padding: '7px 10px', fontSize: '13px' }} />
            </div>
          ))}
          <button onClick={applyCustom} style={{ ...filterPill(false), background: C.accent, color: '#000', padding: '7px 18px' }}>
            ✓ Apply
          </button>
          <button onClick={() => { setPF(''); setPT(''); setPeriod('this_month'); }}
            style={{ ...filterPill(false), padding: '7px 14px' }}>Clear</button>
        </div>
      )}

      {/* ── KPI CARDS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
        <KPICard title="Total Collection"      value={loading ? '—' : fmt(kpis?.total_collection)}      iconBg="#1a3a1a" icon={<svg viewBox="0 0 24 24" fill="none" stroke="#a8e063" strokeWidth="2" width="18" height="18"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>} />
        <KPICard title="Transactions"       value={loading ? '—' : fmtN(kpis?.total_transactions)} iconBg="#1a2a3a" icon={<svg viewBox="0 0 24 24" fill="none" stroke="#56ccf2" strokeWidth="2" width="18" height="18"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>} />
        <KPICard title="Unique Customers"   value={loading ? '—' : fmtN(kpis?.total_customers)}   iconBg="#2a1a3a" icon={<svg viewBox="0 0 24 24" fill="none" stroke="#bb6bd9" strokeWidth="2" width="18" height="18"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>} />
        <KPICard title="Branches Active"    value={loading ? '—' : (kpis?.branch_count ?? '—')}   iconBg="#3a2a1a" icon={<svg viewBox="0 0 24 24" fill="none" stroke="#f2994a" strokeWidth="2" width="18" height="18"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>} />
      </div>

      {/* ── TREND LINE: Collection per Branch ── */}
      <div style={{ ...card, marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <p style={sectionTitle}>Collection Trend by Branch</p>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['daily', 'weekly', 'monthly'].map(p => {
              const disabled = activePeriod === 'all' && p !== 'monthly';
              return (
                <button key={p} style={{ ...toggleBtn(trendPeriod === p), opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
                  onClick={() => !disabled && setGran(p)}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              );
            })}
          </div>
        </div>
        {loading || !lineTrend
          ? <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>
              {loading ? 'Loading...' : 'No data'}
            </div>
          : <>
              <ReactECharts ref={trendChartRef} option={lineTrend} style={{ height: '260px', width: '100%' }} />
              <ChartLegend
                items={trendBranchLabels} colors={BRANCH_COLORS} chartRef={trendChartRef}
                selected={trendLegend.selected}
                onToggle={trendLegend.toggle}
                onSelectAll={trendLegend.selectAll}
                onReset={trendLegend.resetAll}
              />
            </>
        }
      </div>

      {/* ── COLLECTION PIE: Collection by Branch ── */}
      {byBranch.length > 0 && collectionPieOption && (
        <div style={{ ...card, marginBottom: '20px' }}>
          <p style={{ ...sectionTitle, marginBottom: '16px' }}>Collection by Branch</p>
          <ReactECharts ref={collectionPieRef} option={collectionPieOption} style={{ height: '240px', width: '100%' }} />
          <ChartLegend
            items={branchNames}
            colors={BRANCH_COLORS}
            chartRef={collectionPieRef}
            selected={collectionPieLegend.selected}
            onToggle={collectionPieLegend.toggle}
            onSelectAll={collectionPieLegend.selectAll}
            onReset={collectionPieLegend.resetAll}
          />
        </div>
      )}

      {/* ── BAR CHARTS ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div style={card}>
          <p style={{ ...sectionTitle, marginBottom: '16px' }}>Revenue by Branch</p>
          {loading || !barRevenue
            ? <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>
                {loading ? 'Loading...' : 'No data'}
              </div>
            : <ReactECharts option={barRevenue} style={{ height: '220px', width: '100%' }} />
          }
        </div>
        <div style={card}>
          <p style={{ ...sectionTitle, marginBottom: '16px' }}>Transactions by Branch</p>
          {loading || !barTxn
            ? <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>
                {loading ? 'Loading...' : 'No data'}
              </div>
            : <ReactECharts option={barTxn} style={{ height: '220px', width: '100%' }} />
          }
        </div>
      </div>

      {/* ── TABLE: Branch breakdown ── */}
      {byBranch.length > 0 && (
        <div style={{ ...card, marginTop: '20px' }}>
          <p style={{ ...sectionTitle, marginBottom: '16px' }}>Branch Breakdown</p>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <SortTh col="branch_code"      label="Branch"      sortCol={bCol} sortDir={bDir} onSort={bSort} align="left" />
                  <SortTh col="collection"           label="Collection"     sortCol={bCol} sortDir={bDir} onSort={bSort} />
                  <SortTh col="transactions"      label="Transactions" sortCol={bCol} sortDir={bDir} onSort={bSort} />
                  <SortTh col="unique_customers"  label="Customers"   sortCol={bCol} sortDir={bDir} onSort={bSort} />
                  <SortTh col="avg_transaction"   label="Avg / Txn"   sortCol={bCol} sortDir={bDir} onSort={bSort} />
                </tr>
              </thead>
              <tbody>
                {branchSorted.map((b, i) => (
                  <tr key={b.branch_code} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                    <td style={{ padding: '10px 12px', color: C.textPrimary, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: BRANCH_COLORS[byBranch.findIndex(x => x.branch_code === b.branch_code) % BRANCH_COLORS.length], display: 'inline-block' }} />
                      {branchLabel(b.branch_code)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.accent, fontWeight: '700' }}>{fmt(b.collection)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.textSec }}>{fmtN(b.transactions)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.textSec }}>{fmtN(b.unique_customers)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.textMuted }}>{fmt(b.avg_transaction)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}